import { useState, useEffect, useRef } from "react";

type Phase = "typing" | "fading-out" | "idle";

interface UseTypewriterOptions {
  /** Milliseconds per character when typing in. Default: 20 */
  typeSpeed?: number;
  /** Milliseconds for the fade-out transition. Default: 300 */
  fadeDuration?: number;
}

/**
 * Hook that animates text with a typewriter type-in and a fade-out on change.
 * When new text arrives it types in character by character.
 * When the text changes, the old text fades out, then the new text types in.
 */
export function useTypewriter(
  text: string | undefined,
  options: UseTypewriterOptions = {}
) {
  const { typeSpeed = 20, fadeDuration = 300 } = options;

  const [displayed, setDisplayed] = useState("");
  const [phase, setPhase] = useState<Phase>("idle");

  // Refs to keep rAF / setTimeout closures up-to-date
  const targetRef = useRef("");
  const displayedLenRef = useRef(0);
  const rafRef = useRef(0);
  const lastTickRef = useRef(0);
  const fadeTimerRef = useRef<ReturnType<typeof setTimeout>>(undefined);
  const typeSpeedRef = useRef(typeSpeed);
  typeSpeedRef.current = typeSpeed;

  // rAF-driven typing loop — reads everything from refs so the closure never goes stale
  function typeTick(now: number) {
    if (now - lastTickRef.current < typeSpeedRef.current) {
      rafRef.current = requestAnimationFrame(typeTick);
      return;
    }
    lastTickRef.current = now;

    const target = targetRef.current;
    const nextLen = displayedLenRef.current + 1;

    if (nextLen > target.length) {
      setPhase("idle");
      return;
    }

    displayedLenRef.current = nextLen;
    setDisplayed(target.slice(0, nextLen));
    rafRef.current = requestAnimationFrame(typeTick);
  }

  function startTyping() {
    lastTickRef.current = 0;
    setPhase("typing");
    rafRef.current = requestAnimationFrame(typeTick);
  }

  useEffect(() => {
    const newTarget = text ?? "";
    if (newTarget === targetRef.current) return;
    targetRef.current = newTarget;

    cancelAnimationFrame(rafRef.current);
    clearTimeout(fadeTimerRef.current);

    if (displayedLenRef.current === 0) {
      // Nothing on screen — start typing right away
      if (newTarget) {
        startTyping();
      }
    } else {
      // Fade out the current text, then type the new one
      setPhase("fading-out");
      fadeTimerRef.current = setTimeout(() => {
        displayedLenRef.current = 0;
        setDisplayed("");
        if (targetRef.current) {
          startTyping();
        } else {
          setPhase("idle");
        }
      }, fadeDuration);
    }

    return () => {
      cancelAnimationFrame(rafRef.current);
      clearTimeout(fadeTimerRef.current);
      // Reset so a StrictMode re-run (or remount with same text) restarts the animation
      targetRef.current = "";
      displayedLenRef.current = 0;
    };
  }, [text, fadeDuration]);

  return { displayed, phase };
}
